/**
 * Reading and writing 16-bit mono PCM WAV.
 *
 * Hand rolled rather than pulled in, because the whole file format needed here
 * is a 44-byte header and a block of little-endian int16, and a dependency that
 * can handle every WAV variant is a dependency that can also disagree with the
 * Python `wave` module about rounding. The A/B test in this project compares
 * samples, so that disagreement would look like a broken port.
 */

import { SR } from "./engine.js";

/** Float samples in -1..1 to a 16-bit mono WAV, matching Python's `wave`. */
export function encodeWav(buf: Float64Array, sampleRate = SR): Uint8Array {
  const bytes = new Uint8Array(44 + buf.length * 2);
  const view = new DataView(bytes.buffer);

  const ascii = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };

  ascii(0, "RIFF");
  view.setUint32(4, 36 + buf.length * 2, true);
  ascii(8, "WAVE");
  ascii(12, "fmt ");
  view.setUint32(16, 16, true); // PCM header size
  view.setUint16(20, 1, true); // format: PCM
  view.setUint16(22, 1, true); // channels: mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  ascii(36, "data");
  view.setUint32(40, buf.length * 2, true);

  for (let i = 0; i < buf.length; i++) {
    // Clamp then truncate toward zero, which is what int() does in the
    // original. Rounding instead would put every sample up to one LSB out and
    // make a faithful port look unfaithful.
    const clamped = Math.max(-1, Math.min(1, buf[i]));
    view.setInt16(44 + i * 2, Math.trunc(clamped * 32767), true);
  }

  return bytes;
}

export interface DecodedWav {
  sampleRate: number;
  channels: number;
  samples: Float64Array;
}

/** Enough WAV parsing to read back what the Python generator wrote. */
export function decodeWav(bytes: Uint8Array): DecodedWav {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const tag = (offset: number) =>
    String.fromCharCode(...Array.from(bytes.subarray(offset, offset + 4)));

  if (tag(0) !== "RIFF" || tag(8) !== "WAVE") throw new Error("not a RIFF/WAVE file");

  let channels = 1;
  let sampleRate = SR;
  let bitsPerSample = 16;
  let offset = 12;
  let samples: Float64Array | null = null;

  // Chunk walk rather than assuming a 44-byte header: real files carry LIST
  // and fact chunks, and skipping them by guessing an offset reads audio as
  // metadata and produces silence that looks like a rendering bug.
  while (offset + 8 <= bytes.length) {
    const id = tag(offset);
    const size = view.getUint32(offset + 4, true);
    const body = offset + 8;

    if (id === "fmt ") {
      channels = view.getUint16(body + 2, true);
      sampleRate = view.getUint32(body + 4, true);
      bitsPerSample = view.getUint16(body + 14, true);
    } else if (id === "data") {
      if (bitsPerSample !== 16) throw new Error(`only 16-bit supported, got ${bitsPerSample}`);
      const count = Math.floor(size / 2);
      samples = new Float64Array(count);
      for (let i = 0; i < count; i++) samples[i] = view.getInt16(body + i * 2, true) / 32767;
    }

    offset = body + size + (size % 2); // chunks are word aligned
  }

  if (!samples) throw new Error("no data chunk found");
  return { sampleRate, channels, samples };
}
